import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-toastify";

import {
  addTutorial,
  deleteTutorial as deleteTutorialRequest,
  duplicateTutorial as duplicateTutorialRequest,
  getAllTutorials,
  publishTutorial as publishTutorialRequest,
  updateTutorial,
} from "../../../../services/tutorialServices";

import {
  buildTutorialSearchText,
  safeNumber,
} from "../utils/tutorialAdminUtils";

export default function useTutorialsAdmin() {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("updated_desc");

  const [drawerState, setDrawerState] = useState({
    open: false,
    mode: "create",
    tutorial: null,
  });

  const [confirmation, setConfirmation] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const loadTutorials = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const data = await getAllTutorials();

        setTutorials(
          Array.isArray(data) ? data : []
        );
      } catch (error) {
        console.error(
          "Erreur lors du chargement des tutoriels :",
          error
        );

        toast.error(
          "Impossible de charger les tutoriels."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadTutorials();
  }, [loadTutorials]);

  const displayedTutorials = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLowerCase();

    const rows = tutorials.filter((tutorial) => {
      if (
        statusFilter !== "all" &&
        tutorial.statut !== statusFilter
      ) {
        return false;
      }

      if (
        scopeFilter === "global" &&
        tutorial.site_key
      ) {
        return false;
      }

      if (
        scopeFilter === "site" &&
        !tutorial.site_key
      ) {
        return false;
      }

      if (
        normalizedSearch &&
        !buildTutorialSearchText(tutorial).includes(
          normalizedSearch
        )
      ) {
        return false;
      }

      return true;
    });

    rows.sort((a, b) => {
      switch (sortBy) {
        case "version_asc":
          return (
            safeNumber(a.version) -
            safeNumber(b.version)
          );

        case "version_desc":
          return (
            safeNumber(b.version) -
            safeNumber(a.version)
          );

        case "title_asc":
          return String(a.titre || "").localeCompare(
            String(b.titre || ""),
            "fr"
          );

        case "title_desc":
          return String(b.titre || "").localeCompare(
            String(a.titre || ""),
            "fr"
          );

        case "updated_desc":
        default:
          return (
            new Date(b.updated_at || 0).getTime() -
            new Date(a.updated_at || 0).getTime()
          );
      }
    });

    return rows;
  }, [
    tutorials,
    search,
    statusFilter,
    scopeFilter,
    sortBy,
  ]);

  const statistics = useMemo(() => {
    return {
      total: tutorials.length,

      published: tutorials.filter(
        (tutorial) =>
          tutorial.statut === "publie"
      ).length,

      drafts: tutorials.filter(
        (tutorial) =>
          tutorial.statut === "brouillon"
      ).length,

      siteSpecific: tutorials.filter(
        (tutorial) => Boolean(tutorial.site_key)
      ).length,
    };
  }, [tutorials]);

  const openCreateDrawer = useCallback(() => {
    setDrawerState({
      open: true,
      mode: "create",
      tutorial: null,
    });
  }, []);

  const openEditDrawer = useCallback(
    (tutorial) => {
      setDrawerState({
        open: true,
        mode: "edit",
        tutorial,
      });
    },
    []
  );

  const closeDrawer = useCallback(() => {
    setDrawerState({
      open: false,
      mode: "create",
      tutorial: null,
    });
  }, []);

  const saveTutorial = useCallback(
    async (formData) => {
      const isEditing =
        drawerState.mode === "edit" &&
        drawerState.tutorial?.id;

      try {
        setActionLoading(
          isEditing
            ? `update-${drawerState.tutorial.id}`
            : "create"
        );

        const response = isEditing
          ? await updateTutorial(
              drawerState.tutorial.id,
              formData
            )
          : await addTutorial(formData);

        if (!response) {
          throw new Error(
            "La requête n’a retourné aucune donnée."
          );
        }

        toast.success(
          isEditing
            ? "Tutoriel mis à jour."
            : "Tutoriel créé en brouillon."
        );

        closeDrawer();
        await loadTutorials({
          silent: true,
        });

        return true;
      } catch (error) {
        console.error(
          "Erreur lors de l’enregistrement :",
          error
        );

        toast.error(
          "Impossible d’enregistrer le tutoriel."
        );

        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [
      drawerState,
      closeDrawer,
      loadTutorials,
    ]
  );

  const requestPublish = useCallback(
    (tutorial) => {
      setConfirmation({
        type: "publish",
        tutorial,
        title: "Publier cette version ?",
        description:
          "La version actuellement publiée pour cette application et cette portée sera automatiquement archivée.",
        confirmLabel: "Publier",
        tone: "green",
      });
    },
    []
  );

  const requestDuplicate = useCallback(
    (tutorial) => {
      setConfirmation({
        type: "duplicate",
        tutorial,
        title: "Dupliquer ce tutoriel ?",
        description:
          "Une nouvelle version complète sera créée en brouillon avec ses sections, étapes, médias et FAQ.",
        confirmLabel: "Dupliquer",
        tone: "purple",
      });
    },
    []
  );

  const requestDelete = useCallback(
    (tutorial) => {
      setConfirmation({
        type: "delete",
        tutorial,
        title: "Supprimer définitivement ?",
        description:
          "Toutes les sections, étapes, FAQ et références médias de cette version seront supprimées.",
        confirmLabel: "Supprimer",
        tone: "red",
      });
    },
    []
  );

  const closeConfirmation = useCallback(() => {
    if (actionLoading) {
      return;
    }

    setConfirmation(null);
  }, [actionLoading]);

  const confirmRequestedAction =
    useCallback(async () => {
      const tutorial = confirmation?.tutorial;

      if (!tutorial?.id) {
        return;
      }

      const actionKey = `${confirmation.type}-${tutorial.id}`;

      try {
        setActionLoading(actionKey);

        let response = null;

        if (confirmation.type === "publish") {
          response =
            await publishTutorialRequest(
              tutorial.id
            );
        }

        if (confirmation.type === "duplicate") {
          response =
            await duplicateTutorialRequest(
              tutorial.id
            );
        }

        if (confirmation.type === "delete") {
          response =
            await deleteTutorialRequest(
              tutorial.id
            );
        }

        if (!response) {
          throw new Error(
            "La requête n’a retourné aucune donnée."
          );
        }

        const messages = {
          publish:
            "Le tutoriel a été publié.",
          duplicate:
            "Une nouvelle version a été créée.",
          delete:
            "Le tutoriel a été supprimé.",
        };

        toast.success(
          messages[confirmation.type] ||
            "Opération réalisée."
        );

        setConfirmation(null);

        await loadTutorials({
          silent: true,
        });
      } catch (error) {
        console.error(
          "Erreur pendant l’opération :",
          error
        );

        toast.error(
          "L’opération n’a pas pu être réalisée."
        );
      } finally {
        setActionLoading(null);
      }
    }, [confirmation, loadTutorials]);

  const resetFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("all");
    setScopeFilter("all");
    setSortBy("updated_desc");
  }, []);

  return {
    tutorials,
    displayedTutorials,
    statistics,

    loading,
    refreshing,
    actionLoading,

    search,
    setSearch,

    statusFilter,
    setStatusFilter,

    scopeFilter,
    setScopeFilter,

    sortBy,
    setSortBy,

    drawerState,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    saveTutorial,

    confirmation,
    requestPublish,
    requestDuplicate,
    requestDelete,
    closeConfirmation,
    confirmRequestedAction,

    loadTutorials,
    resetFilters,
  };
}